/**
 * COMPOSANT TEMPLATE
 * 
 * Ce fichier montre comment créer un nouveau composant compatible avec le système de grille.
 * 
 * Étapes pour créer un nouveau composant :
 * 1. Copier ce template
 * 2. Renommer le composant et son interface de props
 * 3. Implémenter la logique du composant
 * 4. Créer le fichier _config.ts correspondant
 * 5. Enregistrer dans ComponentRegistry.tsx
 */

import React from 'react';
import { useTheme } from '../../G&CSCM/Core/useTheme';

// Interface des props (paramètres passés depuis la config)
interface TemplateComponentProps {
    title?: string;
    backgroundColor?: string;
    fontSize?: number;
    showBorder?: boolean;
}

/**
 * Composant Template
 * 
 * IMPORTANT : 
 * - Utilise useTheme() pour accéder à la config globale
 * - Les props viennent de config.gridLayout.items[x].props
 * - Le composant doit s'adapter à 100% de largeur et hauteur de sa cellule
 * - Utilise overflow: auto si le contenu peut dépasser
 */
const TemplateComponent: React.FC<TemplateComponentProps> = (props) => {
    // Accès à la config globale et aux thèmes
    const { config } = useTheme();
    
    // Utiliser les props passées ou les valeurs par défaut
    const title = props.title ?? 'Template';
    const backgroundColor = props.backgroundColor ?? 'rgba(0, 123, 255, 0.1)';
    const fontSize = props.fontSize ?? 14;
    const showBorder = props.showBorder ?? true;

    return (
        <div 
            style={{ 
                width: '100%', 
                height: '100%', 
                padding: '16px',
                boxSizing: 'border-box',
                backgroundColor,
                border: showBorder ? '2px solid rgba(0, 123, 255, 0.3)' : 'none',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                overflow: 'auto' // Important : permet le scroll si le contenu dépasse
            }}
        >
            {/* Header */}
            <h3 style={{ 
                margin: 0, 
                fontSize: `${fontSize}px`, 
                color: 'var(--primary-color)' 
            }}>
                {title}
            </h3>

            {/* Contenu du composant */}
            <div style={{ 
                flex: 1, 
                fontSize: `${fontSize - 2}px`,
                lineHeight: 1.5
            }}>
                <p><strong>📦 Composant Template</strong></p>
                <p>Ce composant montre les bonnes pratiques :</p>
                
                <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>✅ Props typées avec interface</li>
                    <li>✅ Valeurs par défaut pour tous les props</li>
                    <li>✅ Utilise useTheme() pour la config globale</li>
                    <li>✅ S'adapte à la taille de la cellule (100%)</li>
                    <li>✅ Overflow auto pour le scroll</li>
                    <li>✅ Utilise les CSS variables du thème</li>
                </ul>

                {/* Affichage de la config globale */}
                <div style={{ 
                    marginTop: '12px',
                    padding: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                    fontSize: '10px'
                }}>
                    <strong>Couleur primaire :</strong> {config.primaryColor}
                    <br />
                    <strong>Mode sombre :</strong> {config.isDark ? 'Oui' : 'Non'}
                </div>
            </div>

            {/* Footer */}
            <div style={{ 
                fontSize: '10px', 
                opacity: 0.6,
                textAlign: 'center',
                paddingTop: '8px',
                borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
                Props: title={title}, backgroundColor={backgroundColor}, fontSize={fontSize}, showBorder={showBorder}
            </div>
        </div>
    );
};

export default TemplateComponent;
